import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import type { AsyncState } from "./stub-data.ts";
import type {
  AccessRequest,
  Agent,
  AgentDnaMetrics,
  Intent,
  Interaction,
  InteractionStep,
  Paged,
  Tool,
} from "./types.ts";
import { EMPTY_METRICS } from "./stub-data.ts";

/**
 * Real operator-API reads for the AgentDNA sections, replacing the `stub-data.ts` placeholders as
 * each endpoint is wired through the contract + `core`. These call the BFF at `/api/v1/*` via
 * `apiFetch` and map the contract's snake_case response shapes to the UI's camelCase domain types
 * (`types.ts`). They return the same `{ data, loading }` shape the stubs did, so a screen swaps its
 * import without further change.
 */

type InteractionPage = components["schemas"]["InteractionPage"];
type ApiInteraction = components["schemas"]["Interaction"];
type ApiInteractionStep = components["schemas"]["InteractionStep"];

const DEFAULT_PAGE_SIZE = 100;

function emptyPage<T>(): Paged<T> {
  return { items: [], total: 0, page: 1, pageSize: DEFAULT_PAGE_SIZE, totalPages: 1 };
}

/** Map a contract InteractionStep to the UI domain type (snake_case → camelCase). */
function mapStep(s: ApiInteractionStep): InteractionStep {
  return {
    direction: s.direction,
    verdict: s.verdict,
    checks: s.checks,
    ...(s.epoch !== undefined ? { epoch: s.epoch } : {}),
    ...(s.latency_ms !== undefined ? { latencyMs: s.latency_ms } : {}),
    ...(s.model !== undefined ? { model: s.model } : {}),
    ...(s.request !== undefined ? { request: s.request } : {}),
    ...(s.response !== undefined ? { response: s.response } : {}),
    ...(s.metadata !== undefined ? { metadata: s.metadata } : {}),
  };
}

/** Map a contract Interaction to the UI domain type (snake_case → camelCase). */
function mapInteraction(i: ApiInteraction): Interaction {
  return {
    id: i.id,
    initiator: { id: i.initiator.id, name: i.initiator.name },
    target: { id: i.target.id, name: i.target.name },
    targetType: i.target_type,
    intent: { id: i.intent.id, name: i.intent.name },
    runtime: i.runtime_ms,
    threat: i.threat,
    created: i.created_at,
    ...(i.block_type !== undefined ? { blockType: i.block_type } : {}),
    ...(i.steps !== undefined ? { steps: i.steps.map(mapStep) } : {}),
  };
}

/** The signed cross-actor interaction ledger (`GET /api/v1/interactions`). */
export function useInteractionsPaged(page = 1): AsyncState<Paged<Interaction>> {
  const query = useQuery<InteractionPage, ApiError>({
    queryKey: ["interactions", page],
    queryFn: ({ signal }) => apiFetch<InteractionPage>("/interactions", { signal }),
    staleTime: 15_000,
  });
  const data: Paged<Interaction> = query.data
    ? {
        items: query.data.items.map(mapInteraction),
        total: query.data.items.length,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 1,
      }
    : emptyPage<Interaction>();
  return { data, loading: query.isPending };
}

/** The interactions of ONE intent (`GET /api/v1/interactions?intent=<id>`), for the intent page. */
export function useInteractionsForIntent(intentId: string): AsyncState<Paged<Interaction>> {
  const query = useQuery<InteractionPage, ApiError>({
    queryKey: ["interactions", "intent", intentId],
    queryFn: ({ signal }) =>
      apiFetch<InteractionPage>("/interactions", { query: { intent: intentId, steps: "1" }, signal }),
    staleTime: 15_000,
    enabled: intentId !== "",
  });
  const data: Paged<Interaction> = query.data
    ? {
        items: query.data.items.map(mapInteraction),
        total: query.data.items.length,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 1,
      }
    : emptyPage<Interaction>();
  return { data, loading: query.isPending };
}

type IntentPage = components["schemas"]["IntentPage"];
type ApiIntent = components["schemas"]["Intent"];
type AgentPage = components["schemas"]["AgentPage"];
type ApiAgent = components["schemas"]["Agent"];
type AppPage = components["schemas"]["AppPage"];
type ApiApp = components["schemas"]["App"];
type AccessRequestPage = components["schemas"]["AccessRequestPage"];
type ApiAccessRequest = components["schemas"]["AccessRequest"];
type ApiAgentActivity = components["schemas"]["AgentActivity"];

function pageOf<A, T>(data: { items: A[] } | undefined, map: (a: A) => T, page: number): Paged<T> {
  if (!data) return emptyPage<T>();
  return {
    items: data.items.map(map),
    total: data.items.length,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  };
}

function mapIntent(i: ApiIntent): Intent {
  return {
    id: i.id,
    name: i.name,
    initiator: { id: i.initiator.id, name: i.initiator.name },
    runtime: i.runtime_ms,
    started: i.started_at,
    agentsInteracted: i.agents_interacted,
    toolsInteracted: i.tools_interacted,
    interactionsCount: i.interactions_count,
    threats: i.threats,
    score: i.score,
    status: i.status,
    provenanceRecordID: i.provenance_record_id,
    reviewStatus: i.review_status,
  };
}

function mapAgent(a: ApiAgent): Agent {
  return {
    id: a.id,
    name: a.name,
    score: a.score,
    created: a.created_at,
    interactions: a.interactions,
    threats: a.threats,
    connected: a.connected,
    status: a.status,
    env: a.env,
    owner: a.owner,
    ...(a.revoked !== undefined ? { revoked: a.revoked } : {}),
  };
}

function mapApp(a: ApiApp): Tool {
  return {
    id: a.id,
    name: a.name,
    score: a.score,
    created: a.created_at,
    interactions: a.interactions,
    threats: a.threats,
    connected: a.connected,
    status: a.status,
    scope: a.scope,
    provider: a.provider,
  };
}

function mapRequest(r: ApiAccessRequest): AccessRequest {
  return {
    id: r.id,
    kind: r.kind,
    subject: r.subject,
    requestedBy: r.requested_by,
    created: r.created_at,
    status: r.status,
    detail: r.detail,
  };
}

/** Intents list (`GET /api/v1/intents`). */
export function useIntentsPaged(page = 1): AsyncState<Paged<Intent>> {
  const query = useQuery<IntentPage, ApiError>({
    queryKey: ["intents", page],
    queryFn: ({ signal }) => apiFetch<IntentPage>("/intents", { signal }),
    staleTime: 15_000,
  });
  return { data: pageOf(query.data, mapIntent, page), loading: query.isPending };
}

/** Agents list (`GET /api/v1/agents`). */
export function useAgentsPaged(page = 1): AsyncState<Paged<Agent>> {
  const query = useQuery<AgentPage, ApiError>({
    queryKey: ["agents", page],
    queryFn: ({ signal }) => apiFetch<AgentPage>("/agents", { signal }),
    staleTime: 15_000,
  });
  return { data: pageOf(query.data, mapAgent, page), loading: query.isPending };
}

/** Apps/tools list (`GET /api/v1/apps`). */
export function useToolsPaged(page = 1): AsyncState<Paged<Tool>> {
  const query = useQuery<AppPage, ApiError>({
    queryKey: ["apps", page],
    queryFn: ({ signal }) => apiFetch<AppPage>("/apps", { signal }),
    staleTime: 15_000,
  });
  return { data: pageOf(query.data, mapApp, page), loading: query.isPending };
}

/** Access/creation requests (`GET /api/v1/requests`). */
export function useRequestsPaged(page = 1): AsyncState<Paged<AccessRequest>> {
  const query = useQuery<AccessRequestPage, ApiError>({
    queryKey: ["requests", page],
    queryFn: ({ signal }) => apiFetch<AccessRequestPage>("/requests", { signal }),
    staleTime: 15_000,
  });
  return { data: pageOf(query.data, mapRequest, page), loading: query.isPending };
}

/** Org-wide AgentDNA headline numbers (`GET /api/v1/agent-activity`). */
export function useAgentDnaMetrics(): AsyncState<AgentDnaMetrics> {
  const query = useQuery<ApiAgentActivity, ApiError>({
    queryKey: ["agent-activity"],
    queryFn: ({ signal }) => apiFetch<ApiAgentActivity>("/agent-activity", { signal }),
    staleTime: 30_000,
  });
  const data: AgentDnaMetrics = query.data
    ? {
        agentCount: query.data.agent_count,
        appCount: query.data.app_count,
        intentCount: query.data.intent_count,
        interactionsCount: query.data.interactions_count,
        threatCount: query.data.threat_count,
        avgReliability: query.data.avg_reliability,
      }
    : EMPTY_METRICS;
  return { data, loading: query.isPending };
}

/** The per-agent risk score with its weighted components (`GET /api/v1/agents/{agent_sub}/risk`). */
export type AgentRisk = components["schemas"]["AgentRisk"];
/** One weighted component of the agent risk score (name, weight, raw value, contribution). */
export type AgentRiskComponent = components["schemas"]["AgentRiskComponent"];

/**
 * Fetch the agent-risk score and its components for ONE agent
 * (`GET /api/v1/agents/{agent_sub}/risk`, operationId `getAgentRisk`, role `viewer`, wired in task
 * 4.6). The query is keyed on the agent id and `enabled` only when an agent is selected, so the
 * read fires when the agent drawer opens and stops when it closes (`agentSub === null`). It returns
 * the generated `AgentRisk` shape unchanged — the drawer's presentation layer maps it to labels and
 * measures. No stub data (requirement 13.6).
 */
export function useAgentRisk(agentSub: string | null): UseQueryResult<AgentRisk, ApiError> {
  return useQuery<AgentRisk, ApiError>({
    queryKey: ["agent-risk", agentSub],
    enabled: agentSub !== null,
    queryFn: ({ signal }) =>
      apiFetch<AgentRisk>(`/agents/${encodeURIComponent(agentSub as string)}/risk`, { signal }),
    staleTime: 30_000,
  });
}

type IntentStatusResult = components["schemas"]["IntentStatusResult"];
type IntentReviewStatusValue = components["schemas"]["IntentReviewStatus"];

/**
 * Set an intent's review status (operator write, audited). On success it invalidates the intents and
 * agent-activity queries so the lists and headline counts reflect the change.
 */
export function useSetIntentReviewStatus(): {
  readonly setStatus: (intentId: string, status: IntentReviewStatusValue) => Promise<Intent>;
  readonly saving: boolean;
  readonly error: ApiError | null;
} {
  const qc = useQueryClient();
  const mutation = useMutation<Intent, ApiError, { intentId: string; status: IntentReviewStatusValue }>({
    mutationFn: async ({ intentId, status }) => {
      const res = await apiFetch<IntentStatusResult>(`/intents/${encodeURIComponent(intentId)}/status`, {
        method: "POST",
        body: { review_status: status },
      });
      return mapIntent(res.intent);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intents"] });
      void qc.invalidateQueries({ queryKey: ["agent-activity"] });
    },
  });
  return {
    setStatus: (intentId, status) => mutation.mutateAsync({ intentId, status }),
    saving: mutation.isPending,
    error: mutation.error ?? null,
  };
}
